import React, { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { FaEdit, FaPlus, FaTrashAlt, FaUserFriends } from "react-icons/fa";

// Accounts the store owner (role_as = 2) creates for their team.
// The API only ever returns this owner's own users (parent_id = owner).
const ROLES = { 1: "Admin", 0: "Customer" };
const EMPTY = { id: null, name: "", email: "", password: "", role_as: 1 };

function firstError(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first.length) return first[0];
  }
  return (data && data.message) || fallback;
}

export default function TeamUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios
      .get("/api/admin/team-users")
      .then((res) => setUsers(res.data.data || []))
      .catch((err) => {
        if (err.response && err.response.status === 403) setDenied(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Team users";
    load();
  }, []);

  const openForm = (user) => {
    setError("");
    setForm(user ? { ...EMPTY, ...user, password: "" } : { ...EMPTY });
  };

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = { name: form.name, email: form.email, role_as: Number(form.role_as) };
    if (form.password) body.password = form.password;
    const request = form.id ? axios.put(`/api/admin/team-users/${form.id}`, body) : axios.post("/api/admin/team-users", body);
    request
      .then((res) => {
        swal("Saved", res.data.message, "success");
        setForm(null);
        load();
      })
      .catch((err) => setError(firstError(err, "The user could not be saved.")))
      .finally(() => setSaving(false));
  };

  const remove = (user) => {
    swal({
      title: `Delete ${user.name}?`,
      text: "They will no longer be able to sign in. This cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      axios
        .delete(`/api/admin/team-users/${user.id}`)
        .then((res) => {
          swal("Deleted", res.data.message, "success");
          load();
        })
        .catch((err) => swal("Error", firstError(err, "The user could not be deleted."), "error"));
    });
  };

  if (denied) {
    return (
      <div className="admin-card p-5 text-center">
        <FaUserFriends size={36} className="text-muted mb-2" />
        <h6 className="fw-bold">Store owner only</h6>
        <p className="text-muted mb-0" style={{ fontSize: "13px" }}>Only the store owner (Super Admin) can create and manage team users.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>Team users</h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Accounts you create for your team. Each person signs in with their own email and password.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openForm(null)}
          className="btn btn-primary rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2"
          style={{ fontSize: "13.5px", background: "var(--admin-primary)", border: "none" }}
        >
          <FaPlus size={12} /> Create user
        </button>
      </div>

      <div className="admin-card">
        <div className="table-responsive">
          {loading ? (
            <div className="p-5 text-center"><div className="spinner-border text-primary" role="status" /></div>
          ) : users.length === 0 ? (
            <div className="p-5 text-center">
              <FaUserFriends size={36} className="text-muted mb-2" />
              <h6 className="fw-bold">No team users yet</h6>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>Create an account for each person who helps run your store.</p>
              <button type="button" className="btn btn-sm btn-dark rounded-pill px-3" onClick={() => openForm(null)}>Create user</button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="fw-semibold">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`admin-badge ${Number(user.role_as) === 1 ? "admin-badge-info" : "admin-badge-success"}`}>
                        {ROLES[Number(user.role_as)] || "User"}
                      </span>
                    </td>
                    <td className="text-muted">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <button type="button" className="btn btn-sm btn-light border" onClick={() => openForm(user)} aria-label={`Edit ${user.name}`}>
                          <FaEdit size={12} /> Edit
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger py-1 px-2" onClick={() => remove(user)} aria-label={`Delete ${user.name}`}>
                          <FaTrashAlt size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {form ? (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15, 23, 42, 0.45)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <form className="modal-content border-0 shadow-lg rounded-3" onSubmit={save}>
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold" style={{ fontSize: "16px" }}>{form.id ? "Edit user" : "Create user"}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setForm(null)} />
              </div>
              <div className="modal-body p-4">
                {error ? <div className="alert alert-danger py-2" style={{ fontSize: "13px" }}>{error}</div> : null}
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="team-name">Name</label>
                  <input id="team-name" className="form-control" required maxLength={191} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="team-email">Email</label>
                  <input id="team-email" type="email" className="form-control" required maxLength={191} autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="team-password">{form.id ? "New password" : "Password"}</label>
                  <input
                    id="team-password"
                    type="password"
                    className="form-control"
                    required={!form.id}
                    minLength={8}
                    autoComplete="new-password"
                    placeholder={form.id ? "Leave empty to keep the current password" : "At least 8 characters"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label fw-semibold" htmlFor="team-role">Role</label>
                  <select id="team-role" className="form-select" value={form.role_as} onChange={(e) => setForm({ ...form, role_as: e.target.value })}>
                    <option value={1}>Admin — can use this store's admin panel</option>
                    <option value={0}>Customer — shop account only</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer border-top bg-light">
                <button type="button" className="btn btn-light border" onClick={() => setForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-dark" disabled={saving}>{saving ? "Saving…" : form.id ? "Save changes" : "Create user"}</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
